// Story 2.2 — query hook for GET /api/v1/admin/portfolios/:portfolio_id.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../../lib/api-client";
import type { AdminPortfolioListRow } from "./useAdminPortfolios";

/** Roster row. Carries NO odds of any kind — neither which-creator
 *  (equal odds, counsel ruling D3) nor which-tier: tier probabilities are
 *  a property of the dossier and live on
 *  `AdminPortfolioDetail.portfolio.tier_distribution`. Hello text is the
 *  only thing that legitimately varies per creator. */
export type AdminPortfolioCreatorRow = {
  portfolio_creator_id: string;
  creator_user_id: string;
  display_name: string | null;
  handle: string | null;
  optional_drop_specific_hello_text: string | null;
  position_in_portfolio: number;
  /** Consent withdrawn — row stays visible for admin truth, but the
   *  creator is out of future reveals and shop roster displays. */
  withdrawn_at: string | null;
};

export type AdminPortfolioDetail = {
  portfolio: AdminPortfolioListRow;
  creators: AdminPortfolioCreatorRow[];
};

export type AdminPortfolioDetailResponse = {
  data: AdminPortfolioDetail;
};

async function fetchAdminPortfolioDetail(
  portfolioId: string,
): Promise<AdminPortfolioDetailResponse> {
  const response = await fetch(`/api/v1/admin/portfolios/${portfolioId}`, {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (response.status === 404) {
    throw new Error("PORTFOLIO_NOT_FOUND");
  }
  if (!response.ok) {
    let message = `Admin portfolio detail fetch failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      // body not JSON
    }
    throw new Error(message);
  }
  return (await response.json()) as AdminPortfolioDetailResponse;
}

export function useAdminPortfolioDetail(portfolioId: string | null) {
  return useQuery<AdminPortfolioDetailResponse, Error>({
    queryKey: ["admin-portfolio", portfolioId],
    queryFn: () => {
      if (!portfolioId) {
        throw new Error("portfolioId required");
      }
      return fetchAdminPortfolioDetail(portfolioId);
    },
    enabled: Boolean(portfolioId),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}
